/* oxlint-disable lingui/no-unlocalized-strings -- shortcut titles/descriptions are translated in the UI via t() */
/**
 * Default command catalogue and key map for Vigía.
 *
 * Commands are declared without `run` handlers — features bind their own
 * runtime behaviour through `useCommandHandler`. The keymap is the single
 * source of truth that the help modal and profile/shortcuts page consume.
 *
 * Title and description strings use plain English here so the Lingui
 * extractor can pick them up directly via `t()` at render time in the UI.
 */

import type { Command, KeyBinding } from "@fleet/shortcuts";

export const COMMAND_IDS = {
  PALETTE_OPEN: "palette.open",
  PALETTE_CLOSE: "palette.close",
  PALETTE_NEXT: "palette.next",
  PALETTE_PREV: "palette.prev",
  PALETTE_CONFIRM: "palette.confirm",

  HELP_OPEN: "help.open",
  HELP_CLOSE: "help.close",

  APP_UNDO: "app.undo",

  UI_TOGGLE_SIDEBAR: "ui.toggle-sidebar",
  UI_TOGGLE_BLUR: "ui.toggle-blur",
  UI_TOGGLE_FOLLOW_DEVICE_THEME: "ui.toggle-follow-device-theme",
  UI_TOGGLE_BACKGROUND_STYLE: "ui.toggle-background-style",

  GOTO_DASHBOARD: "nav.goto-dashboard",
  GOTO_PROFILE: "nav.goto-profile",
  GOTO_THEMES: "nav.goto-themes",
  GOTO_SHORTCUTS: "nav.goto-shortcuts",
  GOTO_LANGUAGE: "nav.goto-language",

  DIALOG_CONFIRM: "dialog.confirm",
  DIALOG_CLOSE: "dialog.close",
} as const;

export const defaultCommands: readonly Command[] = [
  // ── Palette ─────────────────────────────────────────────────────────────
  {
    id: COMMAND_IDS.PALETTE_OPEN,
    category: "Global Actions",
    titleKey: "Search",
  },
  {
    id: COMMAND_IDS.PALETTE_CLOSE,
    category: "Global Actions",
    titleKey: "Close active panels or dialogs",
    when: "palette.open",
  },
  {
    id: COMMAND_IDS.PALETTE_NEXT,
    category: "Global Actions",
    titleKey: "Move focus down in palette",
    when: "palette.open",
  },
  {
    id: COMMAND_IDS.PALETTE_PREV,
    category: "Global Actions",
    titleKey: "Move focus up in palette",
    when: "palette.open",
  },
  {
    id: COMMAND_IDS.PALETTE_CONFIRM,
    category: "Global Actions",
    titleKey: "Open selected result",
    when: "palette.open",
  },

  // ── Undo (ACTIVITY-CORE L1) ─────────────────────────────────────────────
  {
    id: COMMAND_IDS.APP_UNDO,
    category: "Global Actions",
    titleKey: "Undo last action",
    when: "!input.focused && !palette.open && !dialog.open",
  },

  // ── Help ────────────────────────────────────────────────────────────────
  {
    id: COMMAND_IDS.HELP_OPEN,
    category: "Global Actions",
    titleKey: "Open keyboard shortcuts help",
    when: "!input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.HELP_CLOSE,
    category: "Global Actions",
    titleKey: "Close keyboard shortcuts help",
  },

  // ── UI toggles ──────────────────────────────────────────────────────────
  {
    id: COMMAND_IDS.UI_TOGGLE_SIDEBAR,
    category: "Global Actions",
    titleKey: "Toggle sidebar",
    when: "!input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.UI_TOGGLE_BLUR,
    category: "Global Actions",
    titleKey: "Toggle blur effect",
    when: "!input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.UI_TOGGLE_FOLLOW_DEVICE_THEME,
    category: "Global Actions",
    titleKey: "Toggle follow-device theme",
    when: "!input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.UI_TOGGLE_BACKGROUND_STYLE,
    category: "Global Actions",
    titleKey: "Toggle background style (gradients / lines)",
  },

  // ── Navigation chords ──────────────────────────────────────────────────
  {
    id: COMMAND_IDS.GOTO_DASHBOARD,
    category: "Quick Views (Go To)",
    titleKey: "Go to Fleet",
    when: "!input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.GOTO_PROFILE,
    category: "Quick Views (Go To)",
    titleKey: "Go to Profile / Settings",
    when: "!input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.GOTO_THEMES,
    category: "Quick Views (Go To)",
    titleKey: "Go to Themes gallery",
    when: "!input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.GOTO_SHORTCUTS,
    category: "Quick Views (Go To)",
    titleKey: "Go to Shortcuts page",
    when: "!input.focused && !palette.open && !dialog.open",
  },
  {
    id: COMMAND_IDS.GOTO_LANGUAGE,
    category: "Quick Views (Go To)",
    titleKey: "Go to Language settings",
    when: "!input.focused && !palette.open && !dialog.open",
  },

  // ── Dialogs ─────────────────────────────────────────────────────────────
  {
    id: COMMAND_IDS.DIALOG_CONFIRM,
    category: "Dialogs",
    titleKey: "Confirm dialog",
    when: "dialog.open",
  },
  {
    id: COMMAND_IDS.DIALOG_CLOSE,
    category: "Dialogs",
    titleKey: "Close dialog",
    when: "dialog.open",
  },
] as const;

export const defaultKeymap: readonly KeyBinding[] = [
  // ── Palette ─────────────────────────────────────────────────────────────
  { command: COMMAND_IDS.PALETTE_OPEN, keys: "Mod+k" },
  { command: COMMAND_IDS.PALETTE_CLOSE, keys: "Escape", scope: "palette" },
  { command: COMMAND_IDS.PALETTE_NEXT, keys: "ArrowDown", scope: "palette" },
  { command: COMMAND_IDS.PALETTE_PREV, keys: "ArrowUp", scope: "palette" },
  { command: COMMAND_IDS.PALETTE_CONFIRM, keys: "Enter", scope: "palette" },

  // ── Help ────────────────────────────────────────────────────────────────
  { command: COMMAND_IDS.HELP_OPEN, keys: "?" },

  // ── Undo (ACTIVITY-CORE L1) ─────────────────────────────────────────────
  { command: COMMAND_IDS.APP_UNDO, keys: "Mod+z" },

  // ── UI toggles ──────────────────────────────────────────────────────────
  { command: COMMAND_IDS.UI_TOGGLE_SIDEBAR, keys: "Mod+b" },
  { command: COMMAND_IDS.UI_TOGGLE_BLUR, keys: "Mod+Shift+b" },
  { command: COMMAND_IDS.UI_TOGGLE_FOLLOW_DEVICE_THEME, keys: "Mod+Shift+d" },
  // UI_TOGGLE_BACKGROUND_STYLE intentionally has no default binding.

  // ── Navigation chords ──────────────────────────────────────────────────
  { command: COMMAND_IDS.GOTO_DASHBOARD, keys: "g h" },
  { command: COMMAND_IDS.GOTO_PROFILE, keys: "g s" },
  { command: COMMAND_IDS.GOTO_THEMES, keys: "g e" },
  { command: COMMAND_IDS.GOTO_SHORTCUTS, keys: "g k" },
  { command: COMMAND_IDS.GOTO_LANGUAGE, keys: "g l" },

  // ── Dialogs ─────────────────────────────────────────────────────────────
  // NOTE: Dialog Escape is intentionally NOT bound globally. Kobalte's Dialog
  // primitive owns its own Escape behaviour (with focus trap restoration);
  // custom overlays bind locally via useBinding() while they're open.
  { command: COMMAND_IDS.DIALOG_CONFIRM, keys: "Enter", scope: "dialog" },
];
